/* eslint-disable max-lines-per-function */
import React, { useMemo, useRef, useEffect, useState, useCallback } from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './employeeMIS.scss';
import { useAuth } from '../../../Hooks/useAuth';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { EMPLOYEE_MIS } from '../../../Api/resources/endPoint';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';

const PAGE_SIZE = 10;

// Build service URL once (module level, no re-runs)
const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL + url + path;
  }
  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return `${baseUrl}:${port}${url}${path}`;
};

const baseURL = buildServiceURL(serviceName.PROJECT_MS, '/api' + EMPLOYEE_MIS);

const applyToolbarButtonClasses = (rootNode) => {
  if (!rootNode) return;
  const toolbarButtons = rootNode.querySelectorAll('.button-group button');
  toolbarButtons.forEach((buttonElement) => {
    const isResetButton = Boolean(buttonElement.querySelector('.pi-refresh'));
    const isFilterButton = buttonElement.classList.contains('table-utility-filter-btn');
    const isDownloadButton
      = !isFilterButton && !isResetButton && Boolean(buttonElement.querySelector('svg'));
    buttonElement.classList.toggle('employee-mis-reset-btn', isResetButton);
    buttonElement.classList.toggle('employee-mis-export-btn', isDownloadButton);
  });
};

const EmployeeMIS = () => {
  const { userProfile } = useAuth();
  const tableContainerRef = useRef(null);

  const [pagination, setPagination] = useState({
    page: 1,
    limit: PAGE_SIZE,
    total: 0,
  });

  // Response handler — sync pagination from API response
  const handleFilteredApiResponse = useCallback((response) => {
    if (!response) return;

    const paginationData
      = response.data?.pagination
      || response.data?.paginationConfig
      || response.data?.data?.pagination
      || response.data?.data?.paginationConfig;

    const newPage = paginationData?.page ?? paginationData?.currentPage ?? 1;
    const newLimit = paginationData?.limit ?? PAGE_SIZE;
    const newTotal
      = paginationData?.total
      ?? paginationData?.count
      ?? response.meta?.total
      ?? response.data?.tableData?.length
      ?? response.data?.data?.tableData?.length
      ?? 0;

    setPagination((prev) => {
      if (prev.page === newPage && prev.limit === newLimit && prev.total === newTotal) {
        return prev;
      }
      return { page: newPage, limit: newLimit, total: newTotal };
    });
  }, []);

  // Pagination change handler
  const handlePaginationChange = useCallback((newPagination) => {
    setPagination(newPagination);
  }, []);

  // Reset to page 1 on filter/reset actions
  const resetPaginationToFirstPage = useCallback(() => {
    setPagination((prev) => ({ ...prev, page: 1 }));
  }, []);

  // Toolbar button class
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;
    const timer = setTimeout(() => applyToolbarButtonClasses(container), 500);
    const toolbarObserver = new MutationObserver(() => applyToolbarButtonClasses(container));
    toolbarObserver.observe(container, { childList: true, subtree: true });
    return () => {
      clearTimeout(timer);
      toolbarObserver.disconnect();
    };
  }, []);

  // Listen for reset/filter-apply clicks to reset pagination
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const handleResetOrFilterClick = (event) => {
      const isReset = event.target.closest('.employee-mis-reset-btn');
      const isFilterApply = event.target.closest('.tu-filter-footer-apply');
      const isFilterReset = event.target.closest('.tu-filter-footer-reset');

      if ((isReset || isFilterApply || isFilterReset) && container.contains(event.target)) {
        resetPaginationToFirstPage();
      }
    };

    container.addEventListener('click', handleResetOrFilterClick, true);
    return () => container.removeEventListener('click', handleResetOrFilterClick, true);
  }, [resetPaginationToFirstPage]);

  //  Auto right-align numeric columns
  useEffect(() => {
    const container = tableContainerRef.current;
    if (!container) return;

    const applyRightAlign = () => {
      const rows = container.querySelectorAll('.p-datatable-tbody tr');
      const columnStats = new Map();

      rows.forEach((row) => {
        row.querySelectorAll('td').forEach((cell) => {
          const colIndex = cell.cellIndex;
          if (!columnStats.has(colIndex)) {
            columnStats.set(colIndex, { number: 0, text: 0 });
          }
          const stats = columnStats.get(colIndex);
          const cleaned = (cell.textContent ?? '').replace(/\u00A0/g, ' ').trim();
          if (cleaned === '') return;
          const normalized = cleaned.replace(/[−–—]/g, '-');
          const isDash = normalized === '-';
          const numericCandidate = normalized.replace(/,/g, '').replace(/\s+/g, '');
          const isNumber = /^-?\d+(\.\d+)?%?$/.test(numericCandidate);
          if (isNumber) stats.number++;
          else if (!isDash) stats.text++;
        });
      });

      rows.forEach((row) => {
        row.querySelectorAll('td').forEach((cell) => {
          const colIndex = cell.cellIndex;
          const stats = columnStats.get(colIndex);
          if (!stats) return;
          cell.classList.toggle('right-align', stats.number > 0 && stats.text === 0);
        });
      });

      container.querySelectorAll('.p-datatable-thead th').forEach((header) => {
        const stats = columnStats.get(header.cellIndex);
        header.classList.toggle(
          'right-align',
          Boolean(stats?.number > 0 && stats?.text === 0),
        );
      });
    };

    const observer = new MutationObserver(applyRightAlign);
    observer.observe(container, { childList: true, subtree: true, characterData: true });
    applyRightAlign();
    return () => observer.disconnect();
  }, []);

  // Static configuration
  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        headers: { token: userProfile?.access_token || '' },
        additionalPayloadData: {
          employeeId: userProfile?.employeeId || '',
        },
        userInfo: {
          id: userProfile?.id || '',
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || [],
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
        },
        handler: { handleFilteredApiResponse },
        apiEndpoints: {
          getAllViews: { endpoint: '', enabled: false },
          getFilteredData: { endpoint: '', enabled: true },
          saveOrUpdateView: { endpoint: '', enabled: false },
          deleteView: { endpoint: '', enabled: false },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: () => getExportFileName('Employee_MIS'),
            sheetName: () => getExportFileNameWithDate('Employee_MIS'),
          },
        },
        showToolBar: true,
        showPagination: true,
        toolBarSettings: {
          showAddNew: false,
          showFilterOption: true,
          showExpandCollapse: false,
          showDownloadExcel: true,
          downloadExcelSheetName: 'Employee MIS',
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showToolBarHeading: true,
          toolBarHeading: 'Employee MIS',
          showReset: true,
        },
      },
      showFilterOption: true,
      tableSettings: {
        enableTreeTable: false,
        size: 'large',
        loaderSkeleton: { rows: 10, bodyCells: 1, columns: 8 },
        pageSize: PAGE_SIZE,
        paginationConfig: {
          ...pagination,
          onPaginationChange: handlePaginationChange,
          showPageSizeSelector: false,
        },
        showExpandCollapse: false,
        tableScrollHeight: '400px',
        maxFrozenColumns: { desktop: 4, tablet: 2, mobile: 1 },
      },
    }),
    [userProfile, pagination, handleFilteredApiResponse, handlePaginationChange],
  );

  return (
    <div className="employee-mis-outer">
      <div className="employee-mis-table">
        <div className="employee-mis-table-utility" ref={tableContainerRef}>
          <TableUtility configuration={configuration} />
        </div>
      </div>
    </div>
  );
};

export default EmployeeMIS;
